import { findAllEntriesInTable } from 'store/functions/inputFunctions.js';
import { STATIC_TABLE_SETTINGS } from 'store/reducers/constants/constants.js';
import { validateTable } from './validateRow.js';

/*
Strips an example down to a template: its depots, vehicles, vehicle types and
settings stay; its stops and trips go.

This is deleteRowHelper applied to every non-depot location and every trip, done
in one pass. Calling deleteRowHelper once per row gave the same result, but every
call rescans the travel-cost matrix, which already holds (stops x stops) rows. A
168-stop waste example took minutes to clear in a unit test, and every call also
pushed its own undo step.

Semantics deliberately match deleteRowHelper, quirks included:
- a removed location takes with it the trips and travel-cost rows that reference
  it, and its map features (matched on uuid, toUuid and fromUuid);
- a removed trip takes its map feature (matched on uuid);
- a dropdown cell in another table whose display value no longer matches any
  remaining source row is set to null (the key column, e.g. startLocationId, is
  left alone, as deleteRowHelper leaves it), and each touched table is then
  validated once.

Depots are rows with a truthy isDepot. The example builder marks every vehicle
start location that way for both model types, so vehicles keep a valid
"Starting Location".

Mutates and returns `state` (an Immer draft inside the reducer).
*/
export const keepOnlyDepotsHelper = (state) => {
  const data = state.data;
  const locations = data.locations || [];
  const trips = data.trips || [];

  const removedLocationIds = new Set();
  locations.forEach((row) => {
    if (!row.isDepot) removedLocationIds.add(row.UUID);
  });
  const removedTripIds = new Set(trips.map((row) => row.UUID));

  if (removedLocationIds.size === 0 && removedTripIds.size === 0) {
    return state;
  }

  data.locations = locations.filter((row) => row.isDepot);
  data.trips = [];
  if (Array.isArray(data.travel_costs)) {
    data.travel_costs = data.travel_costs.filter((row) =>
      !removedLocationIds.has(row.fromLocationId) && !removedLocationIds.has(row.toLocationId));
  }

  if (state.geoJson) {
    const touchesRemovedLocation = (item) =>
      removedLocationIds.has(item.properties.uuid) ||
      removedLocationIds.has(item.properties.toUuid) ||
      removedLocationIds.has(item.properties.fromUuid);
    if (Array.isArray(state.geoJson.features)) {
      state.geoJson.features = state.geoJson.features.filter((item) =>
        !touchesRemovedLocation(item) && !removedTripIds.has(item.properties.uuid));
    }
    if (Array.isArray(state.geoJson.invalidFeatures)) {
      state.geoJson.invalidFeatures = state.geoJson.invalidFeatures.filter((item) =>
        !touchesRemovedLocation(item));
    }
  }

  const clearedSources = [];
  if (removedLocationIds.size > 0) clearedSources.push('locations');
  if (removedTripIds.size > 0) clearedSources.push('trips');

  const updatedTables = new Set();
  Object.keys(data).forEach((iterTableName) => {
    if (!STATIC_TABLE_SETTINGS[iterTableName]) return;
    const dropdownSources = STATIC_TABLE_SETTINGS[iterTableName]['dropdownSource'];
    if (!dropdownSources || !Array.isArray(data[iterTableName])) return;
    Object.entries(dropdownSources).forEach(([iterColumnName, dropdownSource]) => {
      if (!dropdownSource) return;
      const sourceTable = dropdownSource.tableName;
      // deleteRowHelper never re-checks the table a row was deleted from.
      if (!clearedSources.includes(sourceTable) || sourceTable === iterTableName) return;
      data[iterTableName].forEach((row) => {
        const matches = findAllEntriesInTable(data, sourceTable, [dropdownSource.column], [row[iterColumnName]]);
        if (matches.length === 0) {
          row[iterColumnName] = null;
          updatedTables.add(iterTableName);
        }
      });
    });
  });

  let newState = state;
  updatedTables.forEach((tableName) => {
    newState = validateTable(newState, tableName);
  });
  return newState;
};
